import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Lock, ArrowRight, Shield, Clock, CreditCard, Target, Crown, ShieldAlert, Smartphone, Apple } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-vault">
      {/* Header */}
      <header className="glass-header fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-[#9333EA] flex items-center justify-center">
                <Lock className="h-5 w-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold text-white tracking-tight">
                FutureVault
              </span>
            </div>

            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="nav-login-button" className="text-[#A19DB0] hover:text-white hover:bg-white/5">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button data-testid="nav-signup-button" className="btn-primary">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-none mb-6">
                The app that stops you
                <br />
                <span className="text-[#9333EA]">from spending your money.</span>
              </h1>
              <p className="text-lg text-[#A19DB0] leading-relaxed mb-8 max-w-xl">
                Lock your money in a vault. Pick a date. Can't touch it until then.
                No willpower needed — FutureVault does the discipline for you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  data-testid="hero-get-started-button"
                  onClick={() => navigate('/register')}
                  className="btn-primary h-14 text-lg px-8"
                >
                  Start Saving Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  data-testid="hero-learn-more-button"
                  className="btn-secondary h-14 text-lg px-8"
                >
                  Learn More
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#9333EA]/30 to-transparent rounded-3xl blur-3xl" />
              <img
                src="https://static.prod-images.emergentagent.com/jobs/8fbc2869-29d4-43e4-ba7a-3728a622c8fe/images/a3c614b1e8f5490e9b8c6e88c6d486669cd4c888b6b57e457c31b2750c9b0541.png"
                alt="Secure Vault"
                className="relative z-10 w-full max-w-lg mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
              How It Works
            </h2>
            <p className="text-[#A19DB0] text-lg max-w-2xl mx-auto">
              Three steps. That's it. No complicated setup.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0">
            <FeatureCard
              icon={<Target className="h-6 w-6" />}
              title="Create a Vault"
              description="Name it, set a target amount, and pick your lock date"
            />
            <FeatureCard
              icon={<CreditCard className="h-6 w-6" />}
              title="Deposit with Card"
              description="Add money anytime. It goes straight into your locked vault"
            />
            <FeatureCard
              icon={<Lock className="h-6 w-6" />}
              title="Money is Locked"
              description="Can't touch it. Can't spend it. Until your date arrives"
            />
            <FeatureCard
              icon={<Shield className="h-6 w-6" />}
              title="Withdraw to Bank"
              description="When it unlocks, the money goes right back to your bank"
            />
          </div>
        </div>
      </section>

      {/* Premium Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#9333EA]/20 to-amber-500/20 border border-amber-500/30 mb-4">
              <Crown className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-bold text-amber-400">PREMIUM</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
              For people who are serious about saving
            </h2>
            <p className="text-[#A19DB0] text-lg max-w-2xl mx-auto">
              Commitment vaults, penalties for quitting, and features designed to make you actually follow through.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-0">
            <FeatureCard
              icon={<ShieldAlert className="h-6 w-6" />}
              title="Commitment Vaults"
              description="Withdraw early? You lose 5-20% as a penalty. Real consequences for quitting"
            />
            <FeatureCard
              icon={<Lock className="h-6 w-6" />}
              title="No Escape Vaults"
              description="Zero early withdrawal. Locked until the date. Period. No exceptions"
            />
            <FeatureCard
              icon={<Clock className="h-6 w-6" />}
              title="72-Hour Cooldown"
              description="Want to withdraw? Wait 3 days first. Impulse-proof your money"
            />
          </div>
        </div>
      </section>

      {/* Coming Soon to Mobile - Waitlist */}
      <MobileWaitlistSection />

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="glass-modal p-12 rounded-lg">
            <h2 className="font-heading text-3xl font-bold text-white tracking-tight mb-4">
              Ready to Start Saving?
            </h2>
            <p className="text-[#A19DB0] mb-8">
              Join thousands who are building better savings habits with FutureVault
            </p>
            <Button 
              size="lg" 
              data-testid="cta-get-started-button"
              onClick={() => navigate('/register')}
              className="btn-primary h-14 text-lg px-12"
            >
              Create Free Account
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-md bg-[#9333EA] flex items-center justify-center">
              <Lock className="h-4 w-4 text-white" />
            </div>
            <span className="font-heading text-lg font-bold text-white">FutureVault</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to="/about" className="text-sm text-[#A19DB0] hover:text-white transition-colors" data-testid="footer-about-link">About</Link>
            <Link to="/refunds" className="text-sm text-[#A19DB0] hover:text-white transition-colors" data-testid="footer-refunds-link">Refunds</Link>
            <Link to="/privacy" className="text-sm text-[#A19DB0] hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="text-sm text-[#A19DB0] hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
            <Link to="/support" className="text-sm text-[#A19DB0] hover:text-white transition-colors" data-testid="footer-support-link">Support</Link>
            <p className="text-sm text-[#A19DB0]">
              Built by Arynn Littlejohn
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="border-grid p-8 bg-[#0A0A0F] hover:bg-[#12121A] transition-colors">
      <div className="h-12 w-12 rounded-md bg-[#9333EA]/20 flex items-center justify-center text-[#9333EA] mb-4">
        {icon}
      </div>
      <h3 className="font-heading text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-[#A19DB0] text-sm">{description}</p>
    </div>
  );
}

function MobileWaitlistSection() {
  const [email, setEmail] = useState('');
  const [platform, setPlatform] = useState('ios');
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  // App Store & Play Store links — update these once apps are live
  const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || '';
  const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist/mobile`, { email, platform, source: 'landing' });
      toast.success(res.data.already_subscribed ? "You're already on the list!" : "You're on the list! Check your email.");
      setJoined(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="mobile-waitlist" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="mobile-waitlist-section">
      <div className="max-w-4xl mx-auto">
        <div className="glass-modal rounded-2xl p-8 sm:p-12 border border-[#9333EA]/20 bg-gradient-to-br from-[#9333EA]/10 via-transparent to-transparent">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#9333EA]/20 border border-[#9333EA]/30 mb-4">
                <Smartphone className="h-3 w-3 text-[#9333EA]" />
                <span className="text-xs font-bold text-[#9333EA]">COMING SOON</span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight mb-3">
                FutureVault on your phone.
              </h2>
              <p className="text-[#A19DB0] text-base mb-6">
                iOS and Android apps are on the way. Join the waitlist — we'll email you the second they're live.
              </p>

              {/* Store badges (placeholder until apps are live) */}
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-6">
                <StoreBadge
                  href={APP_STORE_URL}
                  testid="app-store-badge"
                  label="Download on the"
                  store="App Store"
                  icon={<Apple className="h-6 w-6" />}
                />
                <StoreBadge
                  href={PLAY_STORE_URL}
                  testid="play-store-badge"
                  label="Get it on"
                  store="Google Play"
                  icon={<PlayStoreIcon />}
                />
              </div>

              {joined ? (
                <div className="p-4 bg-[#9333EA]/10 border border-[#9333EA]/30 rounded-lg" data-testid="waitlist-success">
                  <p className="text-white font-semibold">You're on the list.</p>
                  <p className="text-[#A19DB0] text-sm mt-1">Check your inbox for a confirmation.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto lg:mx-0" data-testid="waitlist-form">
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="flex-1 bg-black/40 border-white/10 text-white placeholder:text-[#6b6880] h-12"
                    data-testid="waitlist-email-input"
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    className="btn-primary h-12 px-6 whitespace-nowrap"
                    data-testid="waitlist-submit-btn"
                  >
                    {loading ? 'Joining...' : 'Notify me'}
                  </Button>
                </form>
              )}

              {/* Platform selector */}
              {!joined && (
                <div className="flex gap-2 mt-4 justify-center lg:justify-start">
                  {[
                    { key: 'ios', label: 'iOS' },
                    { key: 'android', label: 'Android' },
                    { key: 'both', label: 'Both' },
                  ].map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setPlatform(p.key)}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                        platform === p.key
                          ? 'bg-[#9333EA] border-[#9333EA] text-white'
                          : 'bg-transparent border-white/10 text-[#A19DB0] hover:border-white/30'
                      }`}
                      data-testid={`platform-${p.key}-btn`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Decorative phone illustration */}
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[#9333EA]/20 rounded-full blur-3xl" />
                <div className="relative h-56 w-32 rounded-[2.5rem] border-4 border-white/10 bg-[#0A0A0F] overflow-hidden shadow-2xl shadow-[#9333EA]/30">
                  <div className="h-full w-full bg-gradient-to-b from-[#9333EA]/20 to-transparent p-3 flex flex-col items-center justify-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-[#9333EA] flex items-center justify-center">
                      <Lock className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-center">
                      <p className="text-white text-[10px] font-bold">FutureVault</p>
                      <p className="text-[#A19DB0] text-[8px]">Coming Soon</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StoreBadge({ href, testid, label, store, icon }) {
  const comingSoon = !href;
  const className = `inline-flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${
    comingSoon
      ? 'bg-black/40 border-white/10 text-[#6b6880] cursor-default'
      : 'bg-black border-white/20 hover:border-[#9333EA] text-white'
  }`;
  const content = (
    <>
      <span className={comingSoon ? 'text-[#6b6880]' : 'text-white'}>{icon}</span>
      <span className="flex flex-col items-start leading-tight">
        <span className="text-[10px] opacity-80">{label}</span>
        <span className="text-sm font-semibold">{store}</span>
        {comingSoon && <span className="text-[9px] uppercase tracking-wider text-[#9333EA] font-bold">Coming Soon</span>}
      </span>
    </>
  );
  if (comingSoon) {
    return <div className={className} data-testid={testid}>{content}</div>;
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" className={className} data-testid={testid}>
      {content}
    </a>
  );
}

function PlayStoreIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
      <path d="M3 20.5V3.5c0-.89.52-1.66 1.27-2.01l12.23 10.5L4.27 22.5A2.25 2.25 0 0 1 3 20.5zm14.91-8.09l2.91-1.67c.98-.56.98-1.98 0-2.55L17.91 6.5 14.5 12l3.41 5.5 2.91-1.67c.98-.56.98-1.98 0-2.55l-2.91-1.67zM5.5 22.5l11-6.31L13.5 12 5.5 22.5zm0-21l8 10.5L16.5 8.31 5.5 1.5z"/>
    </svg>
  );
}
